import React from 'react';
import type { User } from '@supabase/supabase-js';
import { Icon, type IconName } from './Icon';
import { SalifyLogoMark } from '../Logo';

export type DashView = 'responses' | 'style' | 'insights' | 'knowledge' | 'integrations' | 'profile' | 'subscription' | 'settings';

interface SidebarProps {
  user: User | null;
  activeView: DashView;
  onNavigate: (view: DashView) => void;
  onSignOut: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ user, activeView, onNavigate, onSignOut }) => {
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  const avatarInitial = (user?.user_metadata?.full_name?.[0] ?? user?.email?.[0] ?? '?').toUpperCase();

  const isSettingsView = activeView === 'profile' || activeView === 'subscription' || activeView === 'settings';

  const defaultNav = [
    { i: "send"     as IconName, label: "Responses",     view: 'responses'    as DashView, meta: "" },
    { i: "sparkle"  as IconName, label: "Your Style",   view: 'style'        as DashView, meta: "" },
    { i: "chart"    as IconName, label: "Insights",     view: 'insights'     as DashView, meta: "" },
    { i: "book"     as IconName, label: "Knowledge",    view: 'knowledge'    as DashView, meta: "" },
    { i: "link"     as IconName, label: "Integrations", view: 'integrations' as DashView, meta: "" },
    { i: "user"     as IconName, label: "Account",      view: 'profile'      as DashView, meta: "" },
  ];

  const settingsNav = [
    { i: "user"     as IconName, label: "Profile",         view: 'profile' as const,      meta: ""  },
    { i: "clipboard" as IconName, label: "Subscription",    view: 'subscription' as const, meta: ""  },
    { i: "settings" as IconName, label: "Settings",        view: 'settings' as const,     meta: ""  },
  ];

  const currentNav = isSettingsView ? settingsNav : defaultNav;

  return (
    <aside className="col sidebar">
      <div className="topbar" style={{ paddingLeft: 20, paddingRight: 16 }}>
        <button className="brand" onClick={() => onNavigate('responses')} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}>
          <SalifyLogoMark size={26} />
          <div className="brand-word">Salifly</div>
        </button>
      </div>

      <nav className="sb-nav">
        {currentNav.map(item => (
          <a
            key={item.label}
            href="#"
            className={`sb-link ${activeView === item.view ? 'sb-link--active' : ''}`}
            onClick={e => { e.preventDefault(); if (item.view) onNavigate(item.view); }}
          >
            <Icon name={item.i} size={18} />
            <span style={{ flex: 1 }}>{item.label}</span>
            {item.meta && <span className="sb-meta">{item.meta}</span>}
          </a>
        ))}
      </nav>

      {!isSettingsView ? (
        <div className="sb-foot-links" style={{ padding: 16, borderTop: '1px solid var(--border-1)', marginTop: 'auto' }}>
          <a href="mailto:support@salifly.com" className="sb-link" style={{ marginBottom: 4 }}>
            <Icon name="message" size={18} />
            <span style={{ flex: 1 }}>Support</span>
          </a>
          <button className="sb-link" onClick={onSignOut} style={{ background: 'transparent', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer', margin: 0, padding: '10px 12px', display: 'flex', alignItems: 'flex-start' }}>
            <span style={{ marginTop: 2, display: 'flex' }}><Icon name="logout" size={18} /></span>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ color: 'var(--fg-1)', fontWeight: 500 }}>Sign out</span>
              <span style={{ color: 'var(--fg-3)', fontSize: '11px', fontWeight: 400, wordBreak: 'break-all' }}>{user?.email}</span>
            </div>
          </button>
        </div>
      ) : (
        <div className="user-widget-container" style={{ position: 'relative', marginTop: 'auto', padding: 16, display: 'flex', justifyContent: 'center' }}>
          {isPopoverOpen && (
            <div className="user-popover">
              <div className="user-popover-header">
                {user?.email}
              </div>
              <div className="user-popover-links">
                <button onClick={() => { onNavigate('profile'); setIsPopoverOpen(false); }}>
                  <Icon name="user" size={16} /> Your account
                </button>
                <button onClick={onSignOut}>
                  <Icon name="logout" size={16} /> Sign out
                </button>
                <a href="mailto:support@salifly.com">
                  <Icon name="message" size={16} /> Support
                </a>
                <button onClick={() => setIsPopoverOpen(false)}>
                  <Icon name="chat" size={16} /> Feedback
                </button>
              </div>
            </div>
          )}
          <button className="avatar-btn" onClick={() => setIsPopoverOpen(!isPopoverOpen)}>
            {avatarInitial}
          </button>
        </div>
      )}
    </aside>
  );
};
